import { StoreProduct, StoreProductVariant } from "@/types/store"
import { useRouter, useSearchParams } from "next/navigation"
import { useEffect, useState } from "react"
import ScrollEffect from "../scroll-effect"
import { normalizeTextEncoding } from "@/lib/util/text-encoding"
import { getProductVariantParam } from "@/lib/util/product-variant-url"

interface VariantSelectPanelProps {
  product: StoreProduct
  selectedVariant?: StoreProductVariant
  setSelectedVariant: (variant: StoreProductVariant) => void
}

const VariantSelectPanel = ({
  product,
  selectedVariant,
  setSelectedVariant,
}: VariantSelectPanelProps) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [showScrollEffectPosition, setShowScrollEffectPosition] = useState({
    left: false,
    right: false,
  })

  const checkScrollability = () => {
    const productContainer = document.getElementById("product-container")
    if (!productContainer) return

    const maxScrollPosition =
      productContainer.scrollWidth - productContainer.clientWidth
    const scrollPosition = productContainer.scrollLeft

    const isScrollableRight = scrollPosition < maxScrollPosition - 2
    const isScrollableLeft = scrollPosition > 0

    setShowScrollEffectPosition({
      left: isScrollableLeft,
      right: isScrollableRight,
    })
  }

  const handleScroll = () => {
    checkScrollability()
  }

  const handleVariantSelect = (variant: StoreProductVariant) => {
    setSelectedVariant(variant)

    // Update URL with selected variant slug
    const params = new URLSearchParams(searchParams?.toString() || "")
    const variantParam = getProductVariantParam(variant.title)
    if (variantParam) {
      params.set("variant", variantParam)
      router.push(`?${params.toString()}`, { scroll: false })
    }
  }

  useEffect(() => {
    checkScrollability()
  }, [product.variants])

  return (
    <div className="w-full overflow-x-auto no-scrollbar">
      <div className="relative">
        <div
          className={`gap-3  mt-2 ${(product.variants?.length ?? 0) < 5
            ? "flex flex-row overflow-x-auto pb-1"
            : "grid auto-rows-max grid-flow-col grid-rows-2 justify-start overflow-y-auto no-scrollbar pb-1"
            }`}
          style={{ minWidth: "100%" }}
          id="product-container"
          onScroll={handleScroll}
        >
          {product.variants?.map((variant, i) => (
            <button
              key={i}
              onClick={() => handleVariantSelect(variant)}
              className={`h-12 min-w-[160px] rounded-xl border-2 px-5 text-base font-semibold transition-all duration-200 ${
                selectedVariant?.id === variant.id
                  ? "border-orange-300 bg-gradient-to-r from-orange-500 to-orange-400 text-white shadow-md shadow-orange-200"
                  : "border-orange-200 bg-slate-50 text-slate-700 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700"
                }`}
            >
              {normalizeTextEncoding(variant.title ?? "")}
            </button>
          ))}
        </div>
        <ScrollEffect
          showScrollEffectPosition={showScrollEffectPosition}
          containerId="product-container"
        />
      </div>
    </div>
  )
}

export default VariantSelectPanel
